import { HeaderValuePair } from './header-value-pair.model';
import { CsvOptionsFileData } from './csv-options-file-data.model';
import { FormFieldPropertyEnum } from '../form-builder/form-field-property-enum.model';

export class CsvOptionsFileLoader {
    // Private constant.
    private static CSV_BYTE_ORDER_MARK_PATTERN = new RegExp('ï»¿');

    // Constructor (cannot use it).
    private constructor() {
    }

    // Static methods.
    public static loadCsvOptionsFile(fileBase64ReadResult: string, propertyName: string, fieldDelimiter: string = ','): CsvOptionsFileData {
        let headers: string[] = [];
        let lines: string[][] = [];
        let linesOfHeaderValuePairs: HeaderValuePair[][] = [];
        let strError: string = null;

        let result: CsvOptionsFileData; 
        let doNotProcessCsv = propertyName === FormFieldPropertyEnum.SELECT_OPTIONS_FILE;

        let rawCsv = null;

        if (fileBase64ReadResult?.length > 0) {
            let fileParts = fileBase64ReadResult.split(',');

            if ((fileParts != null) && (fileParts.length == 2)) {
                let base64Content: string = fileParts[1];
                rawCsv = atob(base64Content);
            }
        }

        if (doNotProcessCsv) {
            result =
            {
                rawCsv: rawCsv,
                headers: null,
                lines: null,
                linesOfHeaderValuePairs: null,
                error: null
            }
        } else {
            CsvOptionsFileLoader.readLineHeaderValuePairs(rawCsv, fieldDelimiter, headers, linesOfHeaderValuePairs, lines);
            
            // Fill in any blank values that can be inferred from other values.
            strError = CsvOptionsFileLoader.replaceBlankValuesWithInferredValues(headers, linesOfHeaderValuePairs);

            // Package and return the result.
            result =
            {
                rawCsv: "",
                headers: headers,
                lines: lines,
                linesOfHeaderValuePairs: linesOfHeaderValuePairs,
                error: strError
            }
        }

        return result;
    }

    // Helper methods.
    private static readLineHeaderValuePairs(
        fileText: string,
        fieldDelimiter: string,
        headers: string[],
        linesOfHeaderValuePairs: HeaderValuePair[][],
        lines: string[][]) 
        : void {
        // Split the lines by carriage returns.
        let allTextLines: string[] = fileText.replace(CsvOptionsFileLoader.CSV_BYTE_ORDER_MARK_PATTERN, '').split(/\r\n|\n/);

        // SWH VNEXT-719:  only load the non-blank headers and, below,
        //                 ignore any additional delimited values; that is,
        //                 process up to at most the number of header 
        //                 values per line of CSV data.
        let localHeaders: string[] = allTextLines[0].split(fieldDelimiter);
        for (let iLocalHeader: number = 0; iLocalHeader < localHeaders.length; iLocalHeader++) {
            let header: string = localHeaders[iLocalHeader];
            if ((header != null) && (header.trim() != ''))
                headers.push(header.trim());
        }

        for (let i = 1; i < allTextLines.length; i++) {
            let data = CsvOptionsFileLoader.csvToArray(allTextLines[i]);

            //if ((data.length == headers.length) && CsvOptionsFileLoader.dataHasNonBlankValue(data)) {
            if ((data.length >= headers.length) && CsvOptionsFileLoader.dataHasNonBlankValue(data)) {
                let tarr = [];
                let lineHeaderValuePairs: HeaderValuePair[] = [];
                for (let j = 0; j < headers.length; j++) {
                    tarr.push(headers[j] + ":" + data[j].trim());

                    let headerValuePair: HeaderValuePair =
                    //{ Index: i + 1, Header: headers[j], ValueText: data[j], ChildValuePairs: null };
                    {
                        Index: i + 1,
                        Header: headers[j],
                        ValueText: data[j].trim(),
                        ChildValuePairs: null
                    };
                    lineHeaderValuePairs.push(headerValuePair);
                }
                lines.push(tarr);
                linesOfHeaderValuePairs.push(lineHeaderValuePairs);
            }
        } // for

        return;
    }

    private static dataHasNonBlankValue(values: string[]): boolean {
        let hasANonBlankValue: boolean = false;

        for (let index: number = 0; index < values.length; index++) {
            if ((values[index] != null) && (values[index].length > 0)) {
                hasANonBlankValue = true;
                break;
            }
        }

        return hasANonBlankValue;
    }

    // pharv - 2/15/22 - added to account for values in the CSV which contain commas
    // See https://stackoverflow.com/questions/8493195/how-can-i-parse-a-csv-string-with-javascript-which-contains-comma-in-data
    private static csvToArray(text): string[] {
        let p = '', row = [''], ret = [row], i = 0, r = 0, s = !0, l;
        for (l of text) {
            if ('"' === l) {
                if (s && l === p) row[i] += l;
                s = !s;
            } else if (',' === l && s) l = row[++i] = '';
            else if ('\n' === l && s) {
                if ('\r' === p) row[i] = row[i].slice(0, -1);
                row = ret[++r] = [l = '']; i = 0;
            } else row[i] += l;
            p = l;
        }
        return ret[0];
    };

    private static replaceBlankValuesWithInferredValues(headers: string[], linesOfHeaderValuePairs: HeaderValuePair[][]): string {
        let strError: string = null;

        let arrPriorValues: string[] = [];
        for (let iHeader: number = 0; iHeader < headers.length; iHeader++) {
            arrPriorValues.push('');
        }

        for (let iLine: number = 0; iLine < linesOfHeaderValuePairs.length; iLine++) {
            let dropDownOptions: HeaderValuePair[] = linesOfHeaderValuePairs[iLine];

            for (let iOption: number = 0; iOption < dropDownOptions.length; iOption++) {
                let headerValuePair: HeaderValuePair = dropDownOptions[iOption];

                // pharv - the following commented out 4/1/2024 following discussion with Dan in Scrum in which he stated that 1) blanks should be treated as valid values
                /*
                if ((headerValuePair.ValueText === null) || (headerValuePair.ValueText.trim() === '')) {
                    if ((arrPriorValues[iOption] !== null) && (arrPriorValues[iOption])) {
                        console.log();
                        headerValuePair.ValueText = arrPriorValues[iOption];
                    } else {
                        let header = headers[iOption];
                        //this.strError = `Missing value for drop-down value '${header}' on line ${iLine} in the options CSV file.`;
                        strError = `Missing value for drop-down value '${header}' on line ${iLine} in the options CSV file.`;

                        break;
                    } // if-else
                } // if
                */
                let valueText = headerValuePair.ValueText ? headerValuePair.ValueText : '';
                arrPriorValues[iOption] = headerValuePair.ValueText;
            } // for
        } // for

        return (strError);
    }

}
